/**
 * Error utility functions for handling API errors in the delivery admin app.
 *
 * ts-rest React Query mutations throw errors that contain the response body
 * with server-provided error messages. These utilities extract those messages
 * for display to users.
 */

interface ApiErrorBody {
  message?: string;
  error?: string;
}

interface ApiError {
  status?: number;
  body?: ApiErrorBody;
  message?: string;
}

/**
 * Extract a user-friendly error message from various error types.
 *
 * Handles:
 * - ts-rest mutation errors with body.message
 * - Standard Error instances
 * - Unknown error types
 *
 * @param error - The caught error object
 * @param fallback - Fallback message if no error message can be extracted
 * @returns User-friendly error message
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;

  if (typeof error === 'object' && error !== null) {
    const apiError = error as ApiError;

    // ts-rest error responses have the message in body.message
    if (apiError.body?.message) {
      return apiError.body.message;
    }

    // Some APIs use body.error instead
    if (apiError.body?.error) {
      return apiError.body.error;
    }

    // Direct message property (e.g., from Error objects)
    if (apiError.message && apiError.message !== 'Error') {
      return apiError.message;
    }
  }

  // Standard Error instance
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}
